import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Chip, Container, Divider, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectAllControls } from '../../redux/reducers/assessmentReducer';
import { selectTitle } from '../../redux/reducers/catalogsReducer';
import { selectControlFindings, selectFindingsList } from '../../redux/reducers/findingsReducer';
import { selectNameList } from '../../redux/reducers/organizationReducer';
import AssessmentFinding from './AssessmentFinding';

const ControlStatus = {
  MAJOR: 'majorFindings',
  MINOR: 'minorFindings',
  INFO: 'infoFindings',
  NO_FINDINGS: 'noFindings',
  list() {
    return [this.MAJOR, this.MINOR, this.INFO, this.NO_FINDINGS];
  },
};

function ReportControl({ control, showCriticality }) {
  function parseSeverity(findingList, criticality) {
    // mangle criticality to resemble finding status
    const criticalityList = findingList.map((f) => `${f.criticality}Findings`);
    let severity = ControlStatus.INFO;
    if (criticality === 'all') {
      if (criticalityList.includes(ControlStatus.MAJOR)) {
        severity = ControlStatus.MAJOR;
      } else if (criticalityList.includes(ControlStatus.MINOR)) {
        severity = ControlStatus.MINOR;
      }
    } else if (criticalityList.includes(criticality)) {
      severity = criticality;
    } else {
      severity = null;
    }
    return severity;
  }

  const { t } = useTranslation();
  const findingsList = useSelector((state) => selectControlFindings(state, control.id));
  const controlStatus = findingsList.length > 0 ? parseSeverity(findingsList, showCriticality) : ControlStatus.NO_FINDINGS;
  const title = useSelector((state) => selectTitle(state, control.id));
  const contactNames = useSelector((state) => selectNameList(state, control.contacts));
  const [expanded, setExpanded] = useState(false);

  const handleExpanded = () => {
    setExpanded(!expanded);
  };

  if (showCriticality === 'all' || showCriticality === controlStatus) {
    return (
      <Paper elevation={4} variant="elevation" subtype="finding">
        <Stack direction="row" justifyContent="space-between" alignItems="center" onClick={() => handleExpanded()}>
          <Typography>{title}</Typography>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Chip
              variant={controlStatus === ControlStatus.NO_FINDINGS ? 'outlined' : 'filled'}
              color={controlStatus === ControlStatus.NO_FINDINGS ? 'primary' : 'controlChip'}
              size="medium"
              subtype={controlStatus}
              label={<Typography variant="chip">{t(`assessment.controlStatus.${controlStatus}`)}</Typography>}
            />
            <IconButton>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
        </Stack>
        {expanded && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Divider />
            <Stack direction="row">
              <Box sx={{ flex: 1 }}>
                <Stack>
                  <Typography>{t('control.evaluation.conclusion.title')}</Typography>
                  <Typography variant="body2">{t(`control.evaluation.conclusion.${control.conclusion}`)}</Typography>
                </Stack>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Stack>
                  <Typography>{t('assessment.done.control.contacts')}</Typography>
                  <Typography variant="body2">{contactNames}</Typography>
                </Stack>
              </Box>
            </Stack>
            {findingsList.length > 0 && (
              <Stack spacing={1}>
                <Typography variant="overline">{t('control.evaluation.findings')}</Typography>
                {findingsList.map((finding) => (
                  <AssessmentFinding key={finding.id} finding={finding} />
                ))}
              </Stack>
            )}
          </Stack>
        )}
      </Paper>
    );
  }
  return (false);
}

function ReportControls() {
  function controlsPerCriticality(findings) {
    const s = new Set(findings.map((finding) => finding.controlId));
    return s.size;
  }

  const { t } = useTranslation();
  const controlList = useSelector(selectAllControls);
  const findings = useSelector(selectFindingsList);
  const [showCriticality, setShowCriticality] = useState('all');
  const [countByCriticality, setCountByCriticality] = useState({
    majorFindings: 0,
    minorFindings: 0,
    infoFindings: 0,
    noFindings: 0,
  });

  const handleSelectCriticality = (value) => {
    setShowCriticality(value);
  };

  useEffect(() => {
    const findingsByCriticality = Object.groupBy(findings, ({ criticality }) => criticality);
    const count = {
      majorFindings: controlsPerCriticality(findingsByCriticality.major ?? []),
      minorFindings: controlsPerCriticality(findingsByCriticality.minor ?? []),
      infoFindings: controlsPerCriticality(findingsByCriticality.info ?? []),
      noFindings: controlList.length - controlsPerCriticality(findings),
    };
    setCountByCriticality(count);
  }, [controlList, findings]);

  return (
    <Container disableGutters sx={{ pb: 0.25 }}>
      <Stack spacing={2}>
        <Typography variant="h6">{t('assessment.report.controls.title')}</Typography>
        <Stack direction="row" spacing={1}>
          {ControlStatus.list().map((criticality) => (
            <Button
              id={`filter-criticality-${criticality}`}
              variant={showCriticality === criticality ? 'contained' : 'outlined'}
              size="small"
              color="secondary"
              key={criticality}
              onClick={() => handleSelectCriticality(criticality)}
            >
              <Typography variant="buttonSmall">
                {`${t(`assessment.controlStatus.${criticality}`)} (${countByCriticality[criticality]})`}
              </Typography>
            </Button>
          ))}
          <Button
            variant={showCriticality === 'all' ? 'contained' : 'outlined'}
            size="small"
            color="secondary"
            id="-show-all"
            key="all"
            onClick={() => handleSelectCriticality('all')}
          >
            <Typography variant="buttonSmall">{t('assessment.findingList.showAll')}</Typography>
          </Button>
        </Stack>
        <Stack spacing={2} sx={{ px: 0.25 }}>
          {controlList.map((control) => (
            <ReportControl key={control.id} control={control} showCriticality={showCriticality} />
          ))}
        </Stack>
      </Stack>
    </Container>
  );
}

export default ReportControls;
